import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Defaults } from 'app/lib/constants';
import { errorToI18n } from 'app/lib/error';
import { SNOWFLAKE_NOT_CONFIGURED } from 'app/store/__generated__/errors';
import { SnowflakeConnectionState } from 'app/lib/types/server';
import { MappedSnowflakeStatusPropType, SnowflakeConnectionPropType } from 'app/lib/proptypes';
import * as styles from './SnowflakeConnectionStatus.css';

const renderTooltipItem = (key, value) =>
    value ? (
        <Fragment key={key}>
            <dt>{key}</dt>
            <dd>{value}</dd>
        </Fragment>
    ) : null;

const DEFAULT_CONNECTION = {
    state: SnowflakeConnectionState.DISCONNECTED
};

export const StatusTooltip = ({
    mappedSnowflakeStatus = Defaults.OBJECT,
    snowflakeConnection = DEFAULT_CONNECTION,
    queryCount = 0,
    executingQueryCount = 0
}) => {
    const { t } = useTranslation();
    const { account, reason, role, warehouse, user } = snowflakeConnection;
    const items = { account, user, role, warehouse };
    const { isUnconfigured, isConnected, isUnconfiguredAdmin } = mappedSnowflakeStatus;

    if (isConnected) {
        return (
            <dl>
                {renderTooltipItem(
                    t('components:SnowflakeConnectionStatus.tooltip.state'),
                    warehouse
                        ? t('components:SnowflakeConnectionStatus.status.connected')
                        : t('components:SnowflakeConnectionStatus.status.connectedNoWarehouse')
                )}
                {Object.entries(items).map(([key, value]) =>
                    renderTooltipItem(
                        t(`components:SnowflakeConnectionStatus.tooltip.${key}`),
                        value
                    )
                )}
                {renderTooltipItem(
                    t('components:SnowflakeConnectionStatus.tooltip.queries'),
                    `${executingQueryCount}/${queryCount}`
                )}
            </dl>
        );
    }

    if (isUnconfigured) {
        const infoKey = isUnconfiguredAdmin ? 'unconfiguredInfoAdmin' : 'unconfiguredInfoNonAdmin';
        return (
            <dl>
                {renderTooltipItem(
                    t('components:SnowflakeConnectionStatus.tooltip.state'),
                    t(SNOWFLAKE_NOT_CONFIGURED, { ns: 'errorFallbacks' })
                )}
                <span className={styles.tooltipValue}>
                    {t(`components:SnowflakeConnectionStatus.tooltip.${infoKey}`)}
                </span>
            </dl>
        );
    }

    if (!reason) {
        return (
            <dl>
                {renderTooltipItem(
                    t('components:SnowflakeConnectionStatus.tooltip.state'),
                    t('components:SnowflakeConnectionStatus.error.NoHealthcheckReceived')
                )}
            </dl>
        );
    }

    const errorTranslation = errorToI18n('components:SnowflakeConnectionStatus.error')(reason);

    return (
        <dl>
            {renderTooltipItem(
                t('components:SnowflakeConnectionStatus.tooltip.state'),
                t(errorTranslation.key, errorTranslation.data)
            )}
            {renderTooltipItem('account', account)}
        </dl>
    );
};

StatusTooltip.propTypes = {
    mappedSnowflakeStatus: MappedSnowflakeStatusPropType,
    snowflakeConnection: SnowflakeConnectionPropType,
    queryCount: PropTypes.number,
    executingQueryCount: PropTypes.number
};
